import type { ChangeEvent, ComponentProps, FC, FormEvent } from 'react';
import { useCallback, useState, useEffect } from 'react';

import { Container, StyledIcon, Input } from './styled';

import { useSearchQuery } from '../../hooks/useSearchQuery';
import { IconName } from '../Icon';

type ISearchProps = ComponentProps<typeof Container> & {
    dimmed?: boolean;
    onChange?: (value: string) => void;
    onSubmit?: (value: string) => void;
};

export const Search: FC<ISearchProps> = ({
    dimmed,
    onSubmit,
    onChange,
    disableShrinking,
    ...containerProps
}) => {
    const [value, setValue] = useState('');
    const [isFocused, setFocused] = useState(false);
    const searchTerm = useSearchQuery();

    // Set initial value
    useEffect(() => {
        searchTerm && setValue(searchTerm);
    }, [searchTerm]);

    const onChangeHandler = useCallback(
        (evt: ChangeEvent<HTMLInputElement>) => {
            setValue(evt.target.value);
            if (onChange) onChange(evt.target.value);
        },
        [onChange]
    );

    const onSubmitHandler = useCallback(
        (evt: FormEvent<HTMLFormElement>) => {
            const target = evt.target as typeof evt.target & {
                search: { value: string };
            };

            if (onSubmit) onSubmit(target.search.value);

            evt.preventDefault();
            evt.stopPropagation();
        },
        [onSubmit]
    );

    return (
        <Container
            isFocused={isFocused}
            disableShrinking={disableShrinking}
            {...containerProps}
        >
            <StyledIcon iconName={IconName.SEARCH} isFocused={isFocused} />
            <form onSubmit={onSubmitHandler}>
                <Input
                    type="search"
                    name="search"
                    placeholder="Search"
                    value={value}
                    onChange={onChangeHandler}
                    onFocus={() => {
                        setFocused(true);
                    }}
                    onBlur={() => {
                        setFocused(false);
                    }}
                    dimmed={dimmed}
                    isFocused={isFocused}
                    disableShrinking={disableShrinking}
                />
            </form>
        </Container>
    );
};

Search.defaultProps = {
    dimmed: false,
    onChange: () => null,
    onSubmit: () => null,
};
